import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Card } from "./ui/card";
import { Activity, ShieldCheck, Zap, Target } from "lucide-react";
import api from "../lib/api";
import { CountUp } from "./CountUp";

// Verdict → color/label map keeps chart consistent w/ existing brand palette
const VERDICT_META = {
    "true":         { label: "True",         color: "#22e0b4", ring: "#22e0b4" },
    "mostly_true":  { label: "Mostly true",  color: "#7cf5c8", ring: "#7cf5c8" },
    "misleading":   { label: "Misleading",   color: "#f5b942", ring: "#f5b942" },
    "false":        { label: "False",        color: "#ff6b6b", ring: "#ff6b6b" },
    "unverified":   { label: "Unverified",   color: "#8890c9", ring: "#8890c9" },
    "safe":         { label: "Safe",         color: "#22e0b4", ring: "#22e0b4" },
    "likely_safe":  { label: "Likely safe",  color: "#7cf5c8", ring: "#7cf5c8" },
    "suspicious":   { label: "Suspicious",   color: "#f5b942", ring: "#f5b942" },
    "scam":         { label: "Scam",         color: "#ff6b6b", ring: "#ff6b6b" },
};

export default function TruthScoreWidget() {
    const [data, setData] = useState(null);
    const [pulseKey, setPulseKey] = useState(0);

    useEffect(() => {
        let mounted = true;
        const load = () =>
            api
                .get("/stats/truth-score")
                .then((r) => {
                    if (!mounted) return;
                    setData(r.data);
                    setPulseKey((k) => k + 1); // trigger heartbeat animation
                })
                .catch(() => {});
        load();
        const t = setInterval(load, 15_000); // live-poll every 15s
        return () => {
            mounted = false;
            clearInterval(t);
        };
    }, []);

    if (!data) return null;

    return (
        <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="truth-score-widget">
            <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/15 px-3 py-1 text-[11px] font-semibold text-accent mb-3">
                        <Activity className="h-3 w-3 text-primary animate-pulse" /> Live · updates every 15s
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl text-accent tracking-tight">
                        Daily Truth Score
                    </h2>
                    <p className="text-sm text-muted-foreground mt-2 max-w-lg">
                        Real-time verdict counts pulled straight from the Real-Check pipeline —
                        no filter, no cherry-picking.
                    </p>
                </div>
                <div className="text-right text-xs text-muted-foreground font-mono uppercase tracking-widest">
                    <div>as of</div>
                    <div className="text-accent">{new Date(data.as_of).toLocaleTimeString()}</div>
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
                {/* Metric 1 — total verified */}
                <StatCard
                    icon={<ShieldCheck className="h-5 w-5 text-primary" />}
                    label="Facts verified · all-time"
                    testid="truth-score-total"
                >
                    <div className="font-display text-5xl font-bold text-accent tabular-nums leading-none">
                        <CountUp value={data.total_all_time} testid="truth-score-total-count" />
                    </div>
                </StatCard>

                {/* Metric 2 — today */}
                <StatCard
                    icon={<Zap className="h-5 w-5 text-emerald-400" />}
                    label="Verified today"
                    testid="truth-score-today"
                    accent
                >
                    <div className="flex items-baseline gap-3">
                        <div className="font-display text-5xl font-bold text-accent tabular-nums leading-none">
                            <CountUp value={data.total_today} testid="truth-score-today-count" />
                        </div>
                        <motion.span
                            key={pulseKey}
                            initial={{ opacity: 1, scale: 1 }}
                            animate={{ opacity: 0, scale: 1.6 }}
                            transition={{ duration: 1.4, ease: "easeOut" }}
                            className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-400 shadow-[0_0_16px_rgba(34,224,180,0.9)]"
                            aria-hidden
                        />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                        <span className="text-emerald-400">●</span> Live pipeline
                    </p>
                </StatCard>

                {/* Metric 3 — accuracy ring */}
                <StatCard
                    icon={<Target className="h-5 w-5 text-primary" />}
                    label="Decisive verdict rate"
                    testid="truth-score-accuracy"
                >
                    <div className="flex items-center gap-4">
                        <AccuracyRing pct={data.accuracy_pct} />
                        <div className="text-xs text-muted-foreground leading-relaxed">
                            <div>
                                <span className="text-accent font-semibold">
                                    <CountUp value={data.decisive_count} />
                                </span>{" "}
                                decisive
                            </div>
                            <div>
                                <span className="text-muted-foreground">
                                    <CountUp value={data.unverified_count} />
                                </span>{" "}
                                unverified
                            </div>
                        </div>
                    </div>
                </StatCard>
            </div>

            {/* Verdict breakdown bar chart */}
            {data.breakdown?.length > 0 && (
                <Card className="mt-5 card-surface rounded-2xl p-6 md:p-7" data-testid="truth-score-breakdown">
                    <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
                        <h3 className="font-display text-lg text-accent">Verdict distribution</h3>
                        <p className="text-xs text-muted-foreground">
                            {data.total_all_time} total checks analyzed
                        </p>
                    </div>

                    {/* Stacked bar */}
                    <div className="h-3 w-full rounded-full bg-white/5 overflow-hidden flex border border-white/10">
                        {data.breakdown.map((b) => {
                            const meta = VERDICT_META[b.verdict] || { color: "#8890c9" };
                            return (
                                <div
                                    key={b.verdict}
                                    className="h-full transition-all duration-700"
                                    style={{
                                        width: `${b.pct}%`,
                                        background: meta.color,
                                        boxShadow: `inset 0 0 8px ${meta.color}55`,
                                    }}
                                    title={`${meta.label}: ${b.count}`}
                                    data-testid={`verdict-bar-${b.verdict}`}
                                />
                            );
                        })}
                    </div>

                    {/* Legend + individual rows */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 mt-5">
                        {data.breakdown.map((b) => {
                            const meta = VERDICT_META[b.verdict] || { label: b.verdict, color: "#8890c9" };
                            return (
                                <div
                                    key={b.verdict}
                                    className="flex items-center gap-2 text-sm"
                                    data-testid={`verdict-legend-${b.verdict}`}
                                >
                                    <span
                                        className="w-2.5 h-2.5 rounded-full shrink-0"
                                        style={{ background: meta.color, boxShadow: `0 0 8px ${meta.color}` }}
                                    />
                                    <span className="text-muted-foreground flex-1">{meta.label}</span>
                                    <span className="font-mono text-accent tabular-nums text-xs">
                                        {b.count} · {b.pct}%
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </Card>
            )}
        </section>
    );
}

function StatCard({ icon, label, children, testid, accent }) {
    return (
        <Card
            className={`card-surface rounded-2xl p-6 relative overflow-hidden ${accent ? "border border-emerald-400/20" : ""}`}
            data-testid={testid}
        >
            {/* corner glow */}
            <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full blur-2xl pointer-events-none ${accent ? "bg-emerald-400/20" : "bg-primary/15"}`} />
            <div className="flex items-center gap-2 mb-4 relative">
                {icon}
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
            </div>
            <div className="relative">{children}</div>
        </Card>
    );
}

function AccuracyRing({ pct }) {
    const size = 76;
    const stroke = 8;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const offset = c * (1 - Math.min(pct, 100) / 100);
    return (
        <div className="relative shrink-0" style={{ width: size, height: size }} aria-label={`${pct}% decisive`}>
            <svg width={size} height={size} className="-rotate-90">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    stroke="rgba(255,255,255,0.08)"
                    strokeWidth={stroke}
                    fill="none"
                />
                <motion.circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    stroke="url(#accuracy-gradient)"
                    strokeWidth={stroke}
                    strokeDasharray={c}
                    strokeLinecap="round"
                    fill="none"
                    initial={{ strokeDashoffset: c }}
                    animate={{ strokeDashoffset: offset }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    style={{ filter: "drop-shadow(0 0 8px rgba(34,224,180,0.6))" }}
                />
                <defs>
                    <linearGradient id="accuracy-gradient" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#22e0b4" />
                        <stop offset="100%" stopColor="#7cf5c8" />
                    </linearGradient>
                </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center font-display text-lg font-bold text-accent">
                {pct}%
            </div>
        </div>
    );
}
